import type { MoreInfoFormData } from '@iheartjane/menu';
import { MoreInfoScreen } from '@iheartjane/menu';
import { Box, Link, Loading, Modal, Typography } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useLocation } from 'react-router';

import { useCustomerDispatch } from '@jane/monolith/customer/redux';
import { getIdentification } from '@jane/monolith/customer/redux/identification';
import { useIsInIframe } from '@jane/shared-ecomm/hooks';
import type { CheckoutLocationState } from '@jane/shared-ecomm/providers';
import { useJaneUser, useUpdateJaneUser } from '@jane/shared/data-access';
import { useHeadlessApp } from '@jane/shared/providers';
import { postMessageToIframeParent } from '@jane/shared/util';

interface Props {
  rewardsLabel?: string | null;
}

export const AddInfoPrompt = ({ rewardsLabel }: Props): ReactElement => {
  const {
    options: { externalAuth },
  } = useHeadlessApp();
  const dispatch = useCustomerDispatch();
  const isInIframe = useIsInIframe();
  const { mutate: updateJaneUser, isPending } = useUpdateJaneUser();
  const { data: janeUser } = useJaneUser();
  const state: Nullable<CheckoutLocationState> = useLocation().state;
  const preAuthCustomer = state?.preAuthCustomer;

  const [openModal, setOpenModal] = useState(false);
  const [showError, setShowError] = useState(false);

  const renderedRewardsLabel = `Add info to see ${rewardsLabel || 'rewards'}`;

  const handleClick = () => {
    if (externalAuth) {
      postMessageToIframeParent({
        messageType: 'authEvent',
        payload: {
          name: 'initiateExternalAuth',
          source: 'bag',
        },
      });
    } else {
      setOpenModal(true);
    }
  };

  const handleUpdate = ({ firstName, lastName, phone }: MoreInfoFormData) => {
    setShowError(false);
    updateJaneUser(
      {
        first_name: firstName,
        last_name: lastName,
        phone,
      },
      {
        onSuccess: () => {
          dispatch(getIdentification());
          setOpenModal(false);
        },
        onError: () => setShowError(true),
      }
    );
  };

  return (
    <Box pt={8} pb={24} pl={2}>
      <Link onClick={() => handleClick()}>{renderedRewardsLabel}</Link>
      {openModal && (
        <Modal
          variant="dialogue"
          open={openModal}
          onRequestClose={() => setOpenModal(false)}
          overlayClose
          topOverride={isInIframe ? '160px' : undefined}
        >
          <Modal.Content>
            <Box maxWidth="279px">
              {isPending ? (
                <Box py={48} position="relative">
                  <Loading color="black" size="lg" />
                </Box>
              ) : (
                <>
                  <MoreInfoScreen
                    continueLabel="Update"
                    onSubmit={handleUpdate}
                    onDoneClick={() => setOpenModal(false)}
                    user={{
                      firstName:
                        janeUser?.user?.first_name ||
                        preAuthCustomer?.firstName,
                      lastName:
                        janeUser?.user?.last_name || preAuthCustomer?.lastName,
                      phone: janeUser?.user?.phone || undefined,
                    }}
                  />
                  {showError && (
                    <Typography mt={16} textAlign="center" color="error">
                      There was an error updating your Jane account. Try again
                      or contact{' '}
                      <Link
                        ariaLabel="link for iheartjane email"
                        href="mailto: support@iheartjane.com"
                        color="error"
                      >
                        support@iheartjane.com
                      </Link>{' '}
                      for more help.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Modal.Content>
        </Modal>
      )}
    </Box>
  );
};
